import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { environmentService } from '../services/environmentService';
import { errorTrackingService } from '../services/errorTrackingService';
import { apiClient } from '../lib/apiClient';
import styles from './ObservabilityPage.module.css';

type TabType = 'overview' | 'errors' | 'logs' | 'sessions';

export default function ObservabilityPage() {
  const { organization } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [errorStats, setErrorStats] = useState<any>(null);
  const [recentErrors, setRecentErrors] = useState<any[]>([]);
  const [recentLogs, setRecentLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [logLevelFilter, setLogLevelFilter] = useState('all');
  const [environmentFilter, setEnvironmentFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, [organization, activeTab, logLevelFilter, environmentFilter]);

  async function loadData() {
    setLoading(true);
    try {
      if (activeTab === 'overview' || activeTab === 'errors') {
        const stats = await errorTrackingService.getErrorStats(organization?.id);
        setErrorStats(stats);

        const errors = await errorTrackingService.getRecentErrors(50);
        setRecentErrors(errors);
      }

      if (activeTab === 'logs') {
        await loadLogs();
      }
    } catch (error) {
      console.error('Failed to load observability data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadLogs() {
    const params = new URLSearchParams()
    params.set('limit', '100')
    if (organization) params.set('orgId', organization.id)
    if (logLevelFilter !== 'all') params.set('level', logLevelFilter)
    if (environmentFilter !== 'all') params.set('environment', environmentFilter)

    const data = await apiClient.get<any[]>(`/application-logs?${params.toString()}`)
    setRecentLogs(data || [])
  }

  function renderOverview() {
    if (loading) {
      return <div className={styles.loading}>Loading statistics...</div>;
    }

    return (
      <div>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Total Errors</div>
            <div className={styles.statValue}>{errorStats?.total || 0}</div>
            <div className={`${styles.statChange} ${styles.negative}`}>
              {errorStats?.last24Hours || 0} in last 24h
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statLabel}>Error Types</div>
            <div className={styles.statValue}>
              {errorStats?.byType ? Object.keys(errorStats.byType).length : 0}
            </div>
            <div className={styles.statChange}>Unique error types</div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statLabel}>Environment</div>
            <div className={styles.statValue}>{environmentService.getEnvironmentLabel()}</div>
            <div className={styles.statChange}>
              Log Level: {environmentService.getLogLevel()}
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statLabel}>Developer Mode</div>
            <div className={styles.statValue}>
              {environmentService.isDeveloperMode() ? 'Enabled' : 'Disabled'}
            </div>
            <div className={styles.statChange}>
              {environmentService.shouldBypassSubscriptionChecks() ? 'Unrestricted access' : 'Production mode'}
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Recent Errors</h2>
          {recentErrors.length === 0 ? (
            <div className={styles.empty}>No errors recorded</div>
          ) : (
            recentErrors.slice(0, 10).map(error => (
              <div key={error.id} className={styles.logEntry}>
                <div className={styles.logHeader}>
                  <span className={`${styles.logLevel} ${styles.error}`}>ERROR</span>
                  <span className={styles.logTimestamp}>
                    {new Date(error.created_at).toLocaleString()}
                  </span>
                </div>
                <div className={styles.logMessage}>
                  <strong>{error.error_type}:</strong> {error.error_message}
                </div>
                <div className={styles.logMeta}>
                  Environment: {error.environment} | URL: {error.url || 'N/A'}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  function renderErrors() {
    if (loading) {
      return <div className={styles.loading}>Loading errors...</div>;
    }

    return (
      <div>
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Error Log</h2>
          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Environment</label>
              <select
                className={styles.select}
                value={environmentFilter}
                onChange={(e) => setEnvironmentFilter(e.target.value)}
              >
                <option value="all">All Environments</option>
                <option value="development">Development</option>
                <option value="sandbox">Sandbox</option>
                <option value="uat">UAT</option>
                <option value="production">Production</option>
              </select>
            </div>
          </div>

          {recentErrors.length === 0 ? (
            <div className={styles.empty}>No errors found</div>
          ) : (
            recentErrors.map(error => (
              <div key={error.id} className={styles.logEntry}>
                <div className={styles.logHeader}>
                  <span className={`${styles.logLevel} ${styles.error}`}>{error.error_type}</span>
                  <span className={styles.logTimestamp}>
                    {new Date(error.created_at).toLocaleString()}
                  </span>
                </div>
                <div className={styles.logMessage}>{error.error_message}</div>
                <div className={styles.logMeta}>
                  Environment: {error.environment} | Session: {error.session_id} | Route: {error.route || 'N/A'}
                </div>
                {error.error_stack && (
                  <details className={styles.errorDetails}>
                    <summary>Stack Trace</summary>
                    <pre className={styles.stackTrace}>{error.error_stack}</pre>
                  </details>
                )}
                {error.breadcrumbs && error.breadcrumbs.length > 0 && (
                  <details className={styles.errorDetails}>
                    <summary>User Actions ({error.breadcrumbs.length})</summary>
                    <div className={styles.breadcrumbs}>
                      {error.breadcrumbs.map((bc: any, idx: number) => (
                        <div key={idx} className={styles.breadcrumbItem}>
                          [{bc.category}] {bc.message}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  function renderLogs() {
    if (loading) {
      return <div className={styles.loading}>Loading logs...</div>;
    }

    return (
      <div>
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Application Logs</h2>
          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Log Level</label>
              <select
                className={styles.select}
                value={logLevelFilter}
                onChange={(e) => setLogLevelFilter(e.target.value)}
              >
                <option value="all">All Levels</option>
                <option value="debug">Debug</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Environment</label>
              <select
                className={styles.select}
                value={environmentFilter}
                onChange={(e) => setEnvironmentFilter(e.target.value)}
              >
                <option value="all">All Environments</option>
                <option value="development">Development</option>
                <option value="sandbox">Sandbox</option>
                <option value="uat">UAT</option>
                <option value="production">Production</option>
              </select>
            </div>
          </div>

          {recentLogs.length === 0 ? (
            <div className={styles.empty}>No logs found</div>
          ) : (
            recentLogs.map(log => (
              <div key={log.id} className={styles.logEntry}>
                <div className={styles.logHeader}>
                  <span className={`${styles.logLevel} ${styles[log.level]}`}>{log.level}</span>
                  <span className={styles.logTimestamp}>
                    {new Date(log.created_at).toLocaleString()}
                  </span>
                </div>
                <div className={styles.logMessage}>
                  [{log.category}] {log.message}
                </div>
                <div className={styles.logMeta}>
                  Environment: {log.environment} | Session: {log.session_id}
                </div>
                {log.error_details && (
                  <details className={styles.errorDetails}>
                    <summary>Error Details</summary>
                    <pre className={styles.stackTrace}>{JSON.stringify(log.error_details, null, 2)}</pre>
                  </details>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  function renderSessions() {
    return (
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>User Sessions</h2>
        <div className={styles.empty}>Session tracking coming soon</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Observability Dashboard</h1>
        <p className={styles.subtitle}>
          Monitor application health, errors, and logs across all environments
        </p>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'errors' ? styles.active : ''}`}
          onClick={() => setActiveTab('errors')}
        >
          Errors
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'logs' ? styles.active : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          Logs
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'sessions' ? styles.active : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          Sessions
        </button>
      </div>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'errors' && renderErrors()}
      {activeTab === 'logs' && renderLogs()}
      {activeTab === 'sessions' && renderSessions()}
    </div>
  );
}
